import React, { useState, useEffect, useMemo } from 'react';
import ClearButton from 'src/components/shared/ClearButton';
import { CircularProgress, TextField } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { UnfoldMore } from '@mui/icons-material';
import Services from 'src/services/Services';
import { toast } from 'react-toastify';
import './EmanetReportPage.css';

const parseLocaleNumber = str => {
    let num = (str ?? '').replace(/[^0-9.,]/g, '');
    const hasDot = num.includes('.');
    const hasComma = num.includes(',');
    if (hasDot && hasComma) {
        num = num.replace(/\./g, '').replace(',', '.');
    } else if (hasDot && !hasComma) {
        num = num.replace(/\./g, '');
    } else if (!hasDot && hasComma) {
        num = num.replace(',', '.');
    }
    return parseFloat(num);
};

const MONTH_KEYS = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];
const MONTH_LABELS = ['Oca', 'Şu', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
export default function EmanetReportPage() {
    const parsedUser = JSON.parse(localStorage.getItem('user'));
    const [reports, setReports] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [sortConfig2, setSortConfig2] = useState({ key: null, direction: 'asc' });
    const [isLoading, setIsLoading] = useState(true);

    const [filterConfig, setFilterConfig] = useState({});
    const [showFilterInput, setShowFilterInput] = useState({});

    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const years = useMemo(() => {
        const start = 2024;
        const now = new Date().getFullYear();
        const arr = [];
        for (let y = start; y <= now; y++) arr.push(y);
        return arr;
    }, []);

    const getDateStringsForYear = year => {
        const start = `${year}-01-01`;
        let end;
        if (year === new Date().getFullYear()) {
            const today = new Date();
            const mm = String(today.getMonth() + 1).padStart(2, '0');
            const dd = String(today.getDate()).padStart(2, '0');
            end = `${today.getFullYear()}-${mm}-${dd}`;
        } else {
            end = `${year}-12-31`;
        }
        return { start, end };
    };

    const toggleFilterInput = key => {
        setShowFilterInput(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleFilterChange = (key, value) => {
        setFilterConfig(prev => ({ ...prev, [key]: value }));
    };

    const clearColumnFilter = key => {
        setFilterConfig(prev => { const newF = { ...prev }; delete newF[key]; return newF; });
        setShowFilterInput(prev => ({ ...prev, [key]: false }));
    };

    useEffect(() => {
        const { start, end } = getDateStringsForYear(selectedYear);
        setIsLoading(true);
        Services.GetEmanetReports(parsedUser.token, start, end)
            .then(res => {
                setIsLoading(false);
                if (res.data.status) {
                    setReports(res.data.result);
                } else {
                    console.log(res.data);
                    toast.error('Rapor çekilirken bir hata oluştu.');
                }
            })
            .catch(err => {
                setIsLoading(false);
                console.error(err);
                toast.error('Rapor çekilirken bir hata oluştu.');
            });
    }, [parsedUser.token, selectedYear]);

    const handleYearChange = e => {
        setSelectedYear(Number(e.target.value));
    };

    const tableRows = useMemo(() => {
        if (!reports.monthly) return [];
        return Object.entries(reports.monthly).map(([month, data], idx) => ({
            id: idx,
            month,
            totalRent: data.totalTransactions,
            totalPrice: data.totalPrice
        }));
    }, [reports.monthly]);

    const sortedTableRows = useMemo(() => {
        const items = [...tableRows];
        if (sortConfig.key) {
            items.sort((a, b) => sortConfig.direction === 'asc'
                ? a[sortConfig.key] - b[sortConfig.key]
                : b[sortConfig.key] - a[sortConfig.key]);
        }
        return items;
    }, [tableRows, sortConfig]);

    const requestSort = key => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const monthlyDataRows = useMemo(() => {
        if (!reports.dropboxCurrentMonthReport) return [];
        return reports.dropboxCurrentMonthReport.map((row, idx) => ({
            id: idx,
            city: row[0],
            location: row[1],
            lenses: row[2],
            rentCount: row[3],
            totalHours: parseFloat(row[4]),
            hoursPerLens: parseFloat(row[5]),
            totalAmount: `₺ ${Number(row[6]).toLocaleString()}`,
            amountPerLens: `₺ ${row[7]}`,
            dailyAvg: `₺ ${row[8]}`
        }));
    }, [reports.dropboxCurrentMonthReport]);

    const requestSort2 = key => {
        let direction = 'asc';
        if (sortConfig2.key === key && sortConfig2.direction === 'asc') direction = 'desc';
        setSortConfig2({ key, direction });
    };

    const checkroomDataRows = useMemo(() => {
        if (!reports.checkroomCurrentMonthReport) return [];
        const daysPassed = Math.max(new Date().getDate() - 1, 1);
        return Object.entries(reports.checkroomCurrentMonthReport).map(([location, data], idx) => ({
            id: monthlyDataRows.length + idx,
            city: '-',
            location,
            lenses: '(v)',
            rentCount: data.totalRentCount,
            totalHours: '-',
            hoursPerLens: (data.totalRentalTime / 3600).toFixed(2),
            totalAmount: `₺ ${data.totalRentCost.toLocaleString()}`,
            amountPerLens: '-',
            dailyAvg: `₺ ${(data.totalRentCost / daysPassed).toFixed(2)}`
        }));
    }, [reports.checkroomCurrentMonthReport, monthlyDataRows]);

    const combinedMonthlyData = useMemo(() => {
        return [...monthlyDataRows, ...checkroomDataRows];
    }, [monthlyDataRows, checkroomDataRows]);

    const filteredMonthlyData = useMemo(() => {
        let items = [...combinedMonthlyData];
        Object.entries(filterConfig).forEach(([key, value]) => {
            items = items.filter(item =>
                item[key]?.toString().toLowerCase().includes(value.toLowerCase())
            );
        });
        return items;
    }, [combinedMonthlyData, filterConfig]);

    const finalMonthlyData = useMemo(() => {
        const items = [...filteredMonthlyData];
        if (sortConfig2.key) {
            items.sort((a, b) => {
                const key = sortConfig2.key;
                const aRaw = a[key];
                const bRaw = b[key];

                const aNum = typeof aRaw === 'string'
                    ? parseLocaleNumber(aRaw)
                    : typeof aRaw === 'number'
                        ? aRaw
                        : NaN;
                const bNum = typeof bRaw === 'string'
                    ? parseLocaleNumber(bRaw)
                    : typeof bRaw === 'number'
                        ? bRaw
                        : NaN;

                if (!isNaN(aNum) && !isNaN(bNum)) {
                    return sortConfig2.direction === 'asc'
                        ? aNum - bNum
                        : bNum - aNum;
                } else {
                    const aStr = aRaw?.toString() ?? '';
                    const bStr = bRaw?.toString() ?? '';
                    return sortConfig2.direction === 'asc'
                        ? aStr.localeCompare(bStr, 'tr')
                        : bStr.localeCompare(aStr, 'tr');
                }
            });
        }
        return items;
    }, [filteredMonthlyData, sortConfig2]);

    const emanetChartData = useMemo(() =>
        MONTH_KEYS.map((key, i) => {
            const d = reports.emanetMonthly?.[key] || {};
            return {
                label: MONTH_LABELS[i],
                transactions: d.totalTransactions || 0,
                price: d.totalPrice || 0
            };
        }), [reports.emanetMonthly]
    );

    const vestiyerChartData = useMemo(() =>
        MONTH_KEYS.map((key, i) => {
            const d = reports.vestiyerMonthly?.[key] || {};
            return {
                label: MONTH_LABELS[i],
                transactions: d.totalTransactions || 0,
                price: d.totalPrice || 0
            };
        }), [reports.vestiyerMonthly]
    );

    const calculatePercentageForPieChart = () => {
        const totalEmanet = emanetChartData.reduce((sum, { transactions }) => sum + transactions, 0);
        const totalVestiyer = vestiyerChartData.reduce((sum, { transactions }) => sum + transactions, 0);
        const total = totalEmanet + totalVestiyer;

        if (total === 0) {
            return [
                { label: 'Emanet', value: 0 },
                { label: 'Vestiyer', value: 0 }
            ];
        }

        return [
            {
                label: 'Emanet',
                value: (totalEmanet / total) * 100
            },
            {
                label: 'Vestiyer',
                value: (totalVestiyer / total) * 100
            }
        ];
    };

    const pieChartData = useMemo(
        () => calculatePercentageForPieChart(),
        [emanetChartData, vestiyerChartData]
    );

    const colors = {
        Emanet: '#ffc0cb',
        Vestiyer: '#f76b8a',
    };

    const [emanetPercent, vestiyerPercent] = pieChartData.map(d => d.value);

    const gradient = `conic-gradient(
        ${colors.Emanet} 0% ${emanetPercent}%,
        ${colors.Vestiyer} ${emanetPercent}% 100%
      )`;

    const xPositions = [75, 140, 205, 270, 335, 400, 465, 530, 595, 660, 725, 790];

    function makeLinePoints(data) {
        const chartH = 300;
        const maxVal = Math.max(...data.map(d => d.transactions));
        if (maxVal <= 0) return '';

        const scale = chartH / maxVal;

        return data.flatMap((d, i) => {
            if (d.transactions <= 0) return [];
            const barHeight = d.transactions * scale;
            const x = xPositions[i];
            const y = chartH - barHeight;
            return [`${x},${y.toFixed(1)}`];
        }).join(' ');
    }

    function getYAxisTicks(dataArray) {
        const maxVal = Math.max(...dataArray);
        if (maxVal <= 0) return [0];

        const magnitude = Math.pow(10, Math.floor(Math.log10(maxVal)));
        const step = magnitude / 2;
        const maxTick = Math.ceil(maxVal / step) * step;

        const ticks = [];
        for (let v = maxTick; v >= 0; v -= step) {
            ticks.push(v);
        }
        return ticks;
    }

    const emanetValues = emanetChartData.map(d => d.transactions);
    const vestiyerValues = vestiyerChartData.map(d => d.transactions);

    const emanetTicks = getYAxisTicks(emanetValues);
    const vestiyerTicks = getYAxisTicks(vestiyerValues);

    function getCurrentMonthName() {
        const now = new Date();
        return now.toLocaleString('tr-TR', { month: 'long' });
    }

    function getCurrentYear() {
        const now = new Date();
        return now.getFullYear();
    }
    return (
        <>
            {
                isLoading ?
                    <div style={{ width: '100%', height: '100%', marginTop: '15px', marginBottom: '15px', alignItems: 'center', justifyContent: 'center', display: 'flex' }
                    }>
                        <CircularProgress size={24} color="error" />
                    </div > :
                    <div>
                        {/* <div className="year-selector-container">
                            <select value={selectedYear} onChange={handleYearChange}>
                                {years.map(year => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </select>
                        </div> */}
                        <div className="total-container">
                            <div className="header"><h1>Toplam Kiralama İşlemi ({getCurrentYear()})</h1></div>
                            <table className="specific-total-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <span style={{ cursor: 'pointer' }} onClick={() => requestSort('month')}>Aylar</span>
                                                <IconButton size="small" sx={{ color: 'white' }} onClick={() => requestSort('month')}>
                                                    <UnfoldMore fontSize="inherit" />
                                                </IconButton>
                                            </div>
                                        </th>
                                        <th>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <span style={{ cursor: 'pointer' }} onClick={() => requestSort('totalRent')}>Toplam Kiralama İşlemi</span>
                                                <IconButton size="small" sx={{ color: 'white' }} onClick={() => requestSort('totalRent')}>
                                                    <UnfoldMore fontSize="inherit" />
                                                </IconButton>
                                            </div>
                                        </th>
                                        <th>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <span style={{ cursor: 'pointer' }} onClick={() => requestSort('totalPrice')}>Toplam Fiyat</span>
                                                <IconButton size="small" sx={{ color: 'white' }} onClick={() => requestSort('totalPrice')}>
                                                    <UnfoldMore fontSize="inherit" />
                                                </IconButton>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedTableRows.map(row => (
                                        <tr key={row.id}>
                                            <td>{row.month}</td>
                                            <td>{row.totalRent}</td>
                                            <td>{row.totalPrice.toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="monthly-container">
                            <div className="header"><h1>{getCurrentMonthName()} Ayı Toplam Kiralama İşlemi ({getCurrentYear()})</h1></div>
                            <table className="specific-monthly-table">
                                <thead>
                                    <tr>
                                        {['city', 'location', 'lenses', 'rentCount', 'totalHours', 'hoursPerLens', 'totalAmount', 'amountPerLens', 'dailyAvg'].map(key => {
                                            const labels = {
                                                city: 'Şehir',
                                                location: 'Lokasyon',
                                                lenses: 'Göz Sayısı',
                                                rentCount: 'Toplam Kiralama',
                                                totalHours: 'Toplam Saat',
                                                hoursPerLens: 'Toplam Saat/Göz',
                                                totalAmount: 'Toplam Tutar',
                                                amountPerLens: 'Toplam Tutar/Göz',
                                                dailyAvg: 'Günlük Kiralama Ortalaması'
                                            };
                                            return (
                                                <th key={key} style={{ padding: '8px 12px' }}>
                                                    {showFilterInput[key] ? (
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                            <TextField
                                                                variant="standard"
                                                                value={filterConfig[key] || ''}
                                                                onChange={e => handleFilterChange(key, e.target.value)}
                                                                placeholder={labels[key]}
                                                                sx={{ width: '100%', backgroundColor: 'white', borderRadius: '8px', paddingInline: '5px' }}
                                                            />
                                                            <ClearButton
                                                                sx={{ backgroundColor: 'white', height: '30px', fontSize: '12px' }}
                                                                onClick={() => clearColumnFilter(key)} />
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                            <span
                                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                onClick={() => toggleFilterInput(key)}
                                                            >
                                                                {labels[key]}
                                                            </span>
                                                            <IconButton
                                                                size="small"
                                                                sx={{ color: 'white' }}
                                                                onClick={() => requestSort2(key)}
                                                            >
                                                                <UnfoldMore fontSize="inherit" />
                                                            </IconButton>
                                                        </div>
                                                    )}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>

                                <tbody>
                                    {finalMonthlyData.map(row => (
                                        <tr key={row.id}>
                                            <td>{row.city}</td>
                                            <td>{row.location}</td>
                                            <td>{row.lenses}</td>
                                            <td>{row.rentCount}</td>
                                            <td>{row.totalHours}</td>
                                            <td>{row.hoursPerLens}</td>
                                            <td>{row.totalAmount}</td>
                                            <td>{row.amountPerLens}</td>
                                            <td>{row.dailyAvg}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="chart-container">
                            <div className="chart-title">Emanet Kiralama İşlemleri Grafiği ({getCurrentYear()})</div>
                            <div>
                                <div className="chart">
                                    <div className="y-axis">
                                        {emanetTicks.map(t => (
                                            <div key={t} className="y-tick">{t}</div>
                                        ))}
                                    </div>
                                    {emanetChartData.map((d, i) => (
                                        <div className="chart-bar" key={i} style={{ marginLeft: i === 0 ? 60 : undefined }}>
                                            <div className="value">{d.transactions}</div>
                                            <div className="bar" style={{ height: d.transactions / 10 }} />
                                            <span>{d.label}</span>
                                        </div>
                                    ))}
                                    <svg className="chart-line" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 300">
                                        <polyline points={makeLinePoints(emanetChartData)} fill="none" stroke="rgba(240,98,145,1)" strokeWidth={4} />
                                    </svg>
                                </div>
                                <div className="label-holder">Aylar</div>
                                <div className="legend">
                                    <span><span className="color-box color-bar" /><span className="legend-title">Toplam Emanet Kiralama İşlemi</span></span>
                                    <span><span className="color-box color-line" /><span className="legend-title">Toplam Emanet Tutarı (₺)</span></span>
                                </div>
                            </div>
                        </div>

                        <div className="chart-container">
                            <div className="chart-title">Vestiyer Kiralama İşlemleri Grafiği ({getCurrentYear()})</div>
                            <div>
                                <div className="chart">
                                    <div className="y-axis">
                                        {vestiyerTicks.map(t => (
                                            <div key={t} className="y-tick">{t}</div>
                                        ))}
                                    </div>
                                    {vestiyerChartData.map((d, i) => (
                                        <div className="chart-bar" key={i} style={{ marginLeft: i === 0 ? '60px' : undefined }}>
                                            <div className="value">{d.transactions}</div>
                                            <div className="second-bar" style={{ height: d.transactions }} />
                                            <span>{d.label}</span>
                                        </div>
                                    ))}
                                    <svg className="chart-line" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 300">
                                        <polyline points={makeLinePoints(vestiyerChartData)} fill="none" stroke="rgba(201,139,106,1)" strokeWidth={4} />
                                    </svg>
                                </div>
                                <div className="label-holder">Aylar</div>
                                <div className="legend">
                                    <span><span className="color-box second-color-bar" /><span className="legend-title">Toplam Vestiyer Kiralama İşlemi</span></span>
                                    <span><span className="color-box second-color-line" /><span className="legend-title">Toplam Vestiyer Tutarı (₺)</span></span>
                                </div>
                            </div>
                        </div>

                        <div className="pie-chart-container">
                            <div className="pie-chart-title">
                                Toplam Kiralama İşlemleri Grafiği ({getCurrentYear()})
                            </div>

                            <div
                                className="pie-chart"
                                style={{ background: gradient }}
                            />

                            <div className="pie-chart-legend">
                                {pieChartData.map(item => (
                                    <div className="pie-chart-legend-item" key={item.label}>
                                        <div
                                            className={`pie-chart-legend-color ${item.label.toLowerCase()}`}
                                            style={{ background: colors[item.label] }}
                                        />
                                        {item.label} (%{Math.round(item.value)})
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

            }
        </>

    )
}
